import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, specDisplay } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCategories } from "@/hooks/useMeta";
import { Page } from "@/components/layout/Layout";
import { ErrorBox, Loading } from "@/components/common/States";
import SpecInput from "@/components/common/SpecInput";
import LocationSelect from "@/components/common/LocationSelect";
import PhotoUploader from "@/components/common/PhotoUploader";
import CatalogPicker from "@/components/listings/CatalogPicker";
import ConditionFields from "@/components/listings/ConditionFields";

const STEPS = ["Kategori ve model", "Teknik özellikler", "İlan bilgileri", "İletişim ve konum"];
const blank = (phone) => ({ category: "", product_id: null, custom_brand: "", custom_model: "", title: "", description: "", price: "",
  currency: "TRY", item_state: "used", condition: { functional_status: "ok" }, photos: [], phone, phone_consent: false, province: "", district: "", spec_values: {} });

export function stepError(step, f, condCheck) {
  if (step === 0 && !f.category) return "Kategori seçin";
  if (step === 0 && !f.product_id && !(f.custom_brand.trim() && f.custom_model.trim())) return "Katalogdan model seçin veya marka/model girin";
  if (step === 2) {
    if (f.title.trim().length < 5) return "Başlık en az 5 karakter olmalı";
    if (f.description.trim().length < 10) return "Açıklama en az 10 karakter olmalı";
    if (!(Number(f.price) > 0)) return "Geçerli bir fiyat girin";
    if (f.item_state === "used" && condCheck?.scheme && (condCheck.warning || condCheck.missing)) return condCheck.warning || condCheck.missing;
  }
  if (step === 3) {
    if (!/^\+?[0-9 ()-]{10,18}$/.test(f.phone)) return "Geçerli bir telefon numarası girin";
    if (!f.province || !f.district) return "İl ve ilçe seçin";
    if (!f.phone_consent) return "Telefon numaranızın yayımlanmasını onaylayın";
  }
  return null;
}

export default function ListingForm() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const { data: cats = [] } = useCategories();
  const [step, setStep] = useState(0);
  const [f, setF] = useState(blank(user?.phone || ""));
  const [catalogSpecs, setCatalogSpecs] = useState({});
  const [custom, setCustom] = useState(false);
  const [condCheck, setCondCheck] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(!!id);
  const cat = cats.find((c) => c.slug === f.category);
  const set = (patch) => setF((s) => ({ ...s, ...patch }));
  useEffect(() => { if (step === 2 && condCheck && !condCheck.warning && !condCheck.missing) setErr(""); }, [condCheck, step]);

  useEffect(() => {
    if (!id) return;
    api.get(`/me/listings/${id}`).then(async ({ data }) => {
      let base = {};
      if (data.product_id) base = (await api.get(`/products/${data.product_id}`)).data.specs;
      setCatalogSpecs(base);
      setCustom(!data.product_id);
      setF({ ...blank(""), ...data, price: String(data.price), condition: data.condition || { functional_status: "ok" },
        photos: data.photos.map((p, i) => ({ id: p, url: data.photo_urls[i] })), phone_consent: true, spec_values: { ...base, ...data.spec_overrides } });
    }).catch((e) => setErr(errMsg(e))).finally(() => setLoading(false));
  }, [id]);

  const pickProduct = (p) => { setCustom(false); setCatalogSpecs(p.specs); set({ product_id: p.id, spec_values: { ...p.specs }, title: f.title || `${p.brand} ${p.model}${p.revision && !p.model.endsWith(p.revision) ? ` ${p.revision}` : ""}` }); };
  const next = () => { const e = stepError(step, f, condCheck); setErr(e || ""); if (!e) setStep(step + 1); };

  const submit = async () => {
    const e = stepError(3, f, condCheck) || stepError(2, f, condCheck);
    if (e) { setErr(e); return; }
    setBusy(true); setErr("");
    const body = { ...f, price: Number(f.price), photos: f.photos.map((p) => p.id), product_id: custom ? null : f.product_id };
    try {
      const { data } = id ? await api.put(`/listings/${id}`, body) : await api.post("/listings", body);
      toast.success(id ? "İlan güncellendi" : "İlanınız yayında!");
      nav(`/ilan/${data.id}`);
    } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };

  if (loading) return <Loading />;
  return (
    <Page className="max-w-3xl">
      <p className="eyebrow">{id ? "İlanı düzenle" : "Yeni ilan"}</p>
      <h1 className="font-display text-3xl sm:text-4xl font-semibold">{STEPS[step]}</h1>
      <div className="mt-6 flex gap-1" data-testid="listing-form-steps">
        {STEPS.map((s, i) => <div key={s} className={`h-1 flex-1 rounded ${i <= step ? "bg-[#0071e3]" : "bg-black/[0.05]"}`} />)}
      </div>
      <div className="mt-8 space-y-6">
        {step === 0 && (
          <>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {cats.map((c) => (
                <button key={c.slug} type="button" data-testid={`form-category-${c.slug}`}
                  onClick={() => { set({ category: c.slug, product_id: null, spec_values: {}, condition: { functional_status: "ok" } }); setCatalogSpecs({}); setCustom(false); }}
                  className={`rounded-md border px-3 py-3 text-left text-sm ${f.category === c.slug ? "border-[#0071e3] bg-[#0071e3]/10" : "border-black/[0.08] hover:border-black/20"}`}>{c.name}</button>
              ))}
            </div>
            {cat && <CatalogPicker category={cat.slug} selectedId={custom ? null : f.product_id} onSelect={pickProduct} custom={custom}
              onCustom={() => { setCustom(true); setCatalogSpecs({}); set({ product_id: null, spec_values: {} }); }} />}
            {custom && (
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5"><Label>Marka</Label><Input data-testid="custom-brand-input" value={f.custom_brand} onChange={(e) => set({ custom_brand: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Model</Label><Input data-testid="custom-model-input" value={f.custom_model} onChange={(e) => set({ custom_model: e.target.value })} /></div>
              </div>
            )}
          </>
        )}
        {step === 1 && cat && (
          <div className="space-y-3" data-testid="spec-editor">
            <p className="flex gap-2 text-sm text-zinc-500"><Info className="h-4 w-4 shrink-0 mt-0.5" />
              {f.product_id ? "Değerler katalogdan otomatik dolduruldu. Değişiklikleriniz yalnızca bu ilanda geçerlidir; ana katalog değişmez." : "Bilmediğiniz alanları boş bırakın; boş alanlar 'bilinmiyor' olarak kaydedilir."}</p>
            {cat.fields.map((fld) => {
              const changed = f.product_id && JSON.stringify(f.spec_values[fld.key] ?? null) !== JSON.stringify(catalogSpecs[fld.key] ?? null);
              return (
                <div key={fld.key} className="grid gap-1.5 sm:grid-cols-[200px_1fr] sm:items-center">
                  <Label className="text-sm text-zinc-700">{fld.label}{fld.unit && <span className="text-zinc-500"> ({fld.unit})</span>}
                    {changed && <span className="ml-2 text-[10px] text-[#0077ed]" data-testid={`spec-changed-${fld.key}`}>değiştirildi · katalog: {specDisplay(fld, catalogSpecs[fld.key]) ?? "bilinmiyor"}</span>}
                  </Label>
                  <SpecInput field={fld} value={f.spec_values[fld.key]} onChange={(v) => set({ spec_values: { ...f.spec_values, [fld.key]: v } })} />
                </div>
              );
            })}
          </div>
        )}
        {step === 2 && (
          <>
            <div className="space-y-1.5"><Label>İlan başlığı</Label><Input data-testid="listing-title-input" value={f.title} onChange={(e) => set({ title: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Açıklama</Label><Textarea data-testid="listing-description-input" rows={5} value={f.description} onChange={(e) => set({ description: e.target.value })} /></div>
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="space-y-1.5"><Label>Fiyat</Label><Input data-testid="listing-price-input" type="number" min="0" value={f.price} onChange={(e) => set({ price: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Para birimi</Label>
                <Select value={f.currency} onValueChange={(v) => set({ currency: v })}><SelectTrigger data-testid="listing-currency-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="TRY">₺ TRY</SelectItem><SelectItem value="USD">$ USD</SelectItem><SelectItem value="EUR">€ EUR</SelectItem></SelectContent></Select></div>
              <div className="space-y-1.5"><Label>Durum</Label>
                <Select value={f.item_state} onValueChange={(v) => set({ item_state: v })}><SelectTrigger data-testid="listing-state-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="new">Sıfır</SelectItem><SelectItem value="used">İkinci el</SelectItem></SelectContent></Select></div>
            </div>
            {f.item_state === "used" && <ConditionFields category={cat} value={f.condition} onChange={(c) => set({ condition: c })} onCheck={setCondCheck} />}
            <div className="space-y-1.5"><Label>Fotoğraflar</Label><PhotoUploader photos={f.photos} onChange={(p) => set({ photos: p })} /></div>
          </>
        )}
        {step === 3 && (
          <>
            <div className="space-y-1.5"><Label>İlanda görünecek telefon numarası</Label><Input data-testid="listing-phone-input" value={f.phone} onChange={(e) => set({ phone: e.target.value })} />
              <p className="text-xs text-zinc-500">Hesabınızdaki numaradan farklı olabilir.</p></div>
            <div className="space-y-1.5"><Label>Konum</Label><LocationSelect prefix="listing" province={f.province} district={f.district} onChange={(p, d) => set({ province: p, district: d })} /></div>
            <div className="rounded-md border border-yellow-500/40 bg-yellow-500/10 p-4 text-sm text-yellow-700" data-testid="phone-disclosure">
              <p className="font-medium">Telefon numaranız hakkında bilgilendirme</p>
              <p className="mt-1 text-yellow-700">Bu numara ilan detayında "Telefon numarasını göster" butonuna basan tüm ziyaretçilere gösterilir. İlanı yayından kaldırdığınızda veya sildiğinizde numara da gösterilmez.</p>
              <label className="mt-3 flex items-center gap-2"><Checkbox data-testid="phone-consent-checkbox" checked={f.phone_consent} onCheckedChange={(c) => set({ phone_consent: !!c })} />Telefon numaramın ilanda yayımlanmasını kabul ediyorum</label>
            </div>
          </>
        )}
        {err && <ErrorBox message={err} />}
        <div className="flex justify-between">
          <Button variant="outline" className="bg-transparent" disabled={step === 0} onClick={() => { setErr(""); setStep(step - 1); }} data-testid="form-back-button">Geri</Button>
          {step < 3 ? <Button onClick={next} className="bg-[#0071e3] hover:bg-[#0077ed] text-white" data-testid="form-next-button">Devam</Button>
            : <Button onClick={submit} disabled={busy} className="bg-[#0071e3] hover:bg-[#0077ed] text-white" data-testid="form-submit-button">{busy ? "Kaydediliyor…" : id ? "Güncelle" : "İlanı yayınla"}</Button>}
        </div>
      </div>
    </Page>
  );
}
